/**
 * B2B Registration Form Enhancement
 * Handles validation, dynamic behavior, and improved UX
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  document.addEventListener('DOMContentLoaded', function() {
    const form = document.getElementById('b2b-register-form');
    if (!form) return;

    // Form elements
    const submitButton = form.querySelector('.b2b-submit-button');
    const requiredFields = form.querySelectorAll('[required]');
    const phoneInput = document.getElementById('RegisterForm-Phone');
    const emailInput = document.getElementById('RegisterForm-Email');
    const passwordInput = document.getElementById('RegisterForm-Password');
    const termsCheckbox = form.querySelector('input[name="terms_accepted"]');

    // Phone number formatting
    if (phoneInput) {
      phoneInput.addEventListener('input', formatPhoneNumber);
    }

    // Email validation
    if (emailInput) {
      emailInput.addEventListener('blur', validateEmail);
    }

    // Password strength indicator
    if (passwordInput) {
      createPasswordStrengthIndicator();
      passwordInput.addEventListener('input', updatePasswordStrength);
    }

    // Form submission
    form.addEventListener('submit', handleFormSubmit);

    // Real-time validation
    requiredFields.forEach(field => {
      field.addEventListener('blur', function() {
        validateField(this);
      });
    });

    /**
     * Format phone number as user types
     */
    function formatPhoneNumber(e) {
      let value = e.target.value.replace(/\D/g, '');
      let formattedValue = '';

      if (value.length > 0) {
        if (value.length <= 3) {
          formattedValue = `(${value}`;
        } else if (value.length <= 6) {
          formattedValue = `(${value.slice(0, 3)}) ${value.slice(3)}`;
        } else {
          formattedValue = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
        }
      }

      e.target.value = formattedValue;
    }

    /**
     * Validate email format
     */
    function validateEmail() {
      const email = emailInput.value;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      
      if (email && !emailRegex.test(email)) {
        showFieldError(emailInput, 'Please enter a valid email address');
      } else {
        clearFieldError(emailInput);
      }
    }

    /**
     * Create password strength indicator
     */
    function createPasswordStrengthIndicator() {
      const container = passwordInput.parentElement;
      const indicator = document.createElement('div');
      indicator.className = 'password-strength-indicator';
      indicator.innerHTML = `
        <div class="password-strength-bar">
          <div class="password-strength-fill"></div>
        </div>
        <span class="password-strength-text"></span>
      `;
      container.appendChild(indicator);
    }

    /**
     * Update password strength indicator
     */
    function updatePasswordStrength() {
      const password = passwordInput.value;
      const strengthBar = document.querySelector('.password-strength-fill');
      const strengthText = document.querySelector('.password-strength-text');
      
      let strength = 0;
      const checks = {
        length: password.length >= 8,
        lowercase: /[a-z]/.test(password),
        uppercase: /[A-Z]/.test(password),
        numbers: /\d/.test(password),
        special: /[^A-Za-z0-9]/.test(password)
      };

      // Calculate strength
      Object.values(checks).forEach(passed => {
        if (passed) strength += 20;
      });

      // Update UI
      strengthBar.style.width = strength + '%';
      
      if (strength < 40) {
        strengthBar.style.backgroundColor = '#D00';
        strengthText.textContent = 'Weak';
      } else if (strength < 80) {
        strengthBar.style.backgroundColor = '#FF8C42';
        strengthText.textContent = 'Medium';
      } else {
        strengthBar.style.backgroundColor = '#5B8A3A';
        strengthText.textContent = 'Strong';
      }
    }

    /**
     * Validate individual field
     */
    function validateField(field) {
      if (field.hasAttribute('required') && !field.value.trim()) {
        showFieldError(field, 'This field is required');
        return false;
      }
      
      clearFieldError(field);
      return true;
    }

    /**
     * Show field error
     */
    function showFieldError(field, message) {
      clearFieldError(field);
      
      const error = document.createElement('span');
      error.className = 'b2b-field-error';
      error.textContent = message;
      error.id = field.id + '-error';
      
      field.setAttribute('aria-invalid', 'true');
      field.setAttribute('aria-describedby', error.id);
      field.parentElement.appendChild(error);
    }

    /**
     * Clear field error
     */
    function clearFieldError(field) {
      const existingError = field.parentElement.querySelector('.b2b-field-error');
      if (existingError) {
        existingError.remove();
        field.removeAttribute('aria-invalid');
        field.removeAttribute('aria-describedby');
      }
    }

    /**
     * Handle form submission
     */
    function handleFormSubmit(e) {
      let isValid = true;

      // Validate all required fields
      requiredFields.forEach(field => {
        if (!validateField(field)) {
          isValid = false;
        }
      });

      // Check terms acceptance
      if (termsCheckbox && !termsCheckbox.checked) {
        e.preventDefault();
        alert('Please accept the wholesale terms and conditions to continue.');
        return false;
      }

      // If validation fails, prevent submission
      if (!isValid) {
        e.preventDefault();
        
        // Scroll to first error
        const firstError = form.querySelector('[aria-invalid="true"]');
        if (firstError) {
          firstError.focus();
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        
        return false;
      }

      // Add loading state
      form.classList.add('b2b-loading');
      submitButton.disabled = true;
      submitButton.textContent = 'Submitting...';

      // Store company data in customer notes
      // This will be processed by Shopify and can be used to create company accounts
      const companyData = {
        companyName: form.querySelector('[name="customer[note][Company Name]"]').value,
        taxId: form.querySelector('[name="customer[note][Tax ID]"]').value,
        businessType: form.querySelector('[name="customer[note][Business Type]"]').value,
        storeLocations: form.querySelector('[name="customer[note][Store Locations]"]').value,
        annualRevenue: form.querySelector('[name="customer[note][Annual Revenue]"]').value,
        phone: form.querySelector('[name="customer[note][Phone]"]').value,
        jobTitle: form.querySelector('[name="customer[note][Job Title]"]').value,
        howHeard: form.querySelector('[name="customer[note][How Heard]"]').value,
        comments: form.querySelector('[name="customer[note][Comments]"]').value,
        submittedAt: new Date().toISOString()
      };

      // Add formatted company data to a hidden field
      const companyDataField = document.createElement('input');
      companyDataField.type = 'hidden';
      companyDataField.name = 'customer[note]';
      companyDataField.value = formatCompanyDataForNote(companyData);
      form.appendChild(companyDataField);
    }

    /**
     * Format company data for storage in customer note
     */
    function formatCompanyDataForNote(data) {
      return `
B2B WHOLESALE APPLICATION
========================
Company Name: ${data.companyName}
Tax ID: ${data.taxId || 'Not provided'}
Business Type: ${data.businessType}
Store Locations: ${data.storeLocations || 'Not specified'}
Annual Revenue: ${data.annualRevenue || 'Not specified'}
Phone: ${data.phone}
Job Title: ${data.jobTitle || 'Not specified'}
How They Heard: ${data.howHeard || 'Not specified'}
Comments: ${data.comments || 'None'}
Submitted: ${data.submittedAt}
========================
      `.trim();
    }
  });

  // Add password strength indicator styles
  const style = document.createElement('style');
  style.textContent = `
    .password-strength-indicator {
      margin-top: 8px;
    }
    
    .password-strength-bar {
      height: 4px;
      background-color: #E5E5E5;
      border-radius: 2px;
      overflow: hidden;
      margin-bottom: 4px;
    }
    
    .password-strength-fill {
      height: 100%;
      width: 0;
      transition: width 0.3s, background-color 0.3s;
    }
    
    .password-strength-text {
      font-size: 12px;
      color: #666;
    }
  `;
  document.head.appendChild(style);
})();